import React, { useRef, useEffect, useState } from 'react';

const Philosophy = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const principles = [
    {
      title: 'Intelligence over Automation',
      description: 'AI that thinks, not replaces',
      detail: 'We build systems that augment human intelligence rather than replace human judgment. Our AI understands context, learns from interactions, and provides insights that enhance decision-making.'
    },
    {
      title: 'Simplicity at Scale',
      description: 'Modular systems that grow with you',
      detail: 'Complex problems require elegant solutions. Our modular architecture ensures that as your organization grows, our systems scale seamlessly without adding complexity.'
    },
    {
      title: 'Trust by Design',
      description: 'Privacy and transparency in every layer',
      detail: 'Trust is earned through transparency. Every algorithm, every data flow, and every decision point in our systems is designed with privacy, security, and explainability at its core.'
    }
  ];

  const coreValues = [
    {
      title: 'Human-Centered Design',
      description: 'Technology should serve people, not the other way around.',
      detail: 'Every interface, every algorithm, and every decision is designed with human needs at the center.'
    },
    {
      title: 'Ethical AI Development',
      description: 'Responsible innovation for sustainable impact.',
      detail: 'We prioritize ethical considerations in all AI development, ensuring fairness, transparency, and accountability.'
    },
    {
      title: 'Continuous Learning',
      description: 'Evolving with the world around us.',
      detail: 'Our systems and our team continuously adapt and learn from new challenges and opportunities.'
    }
  ];

  return (
    <section className="philosophy" ref={sectionRef}>
      <div className="container">
        <div className={`section-content ${isVisible ? 'section-content--visible' : ''}`}>
          {/* Hero Section with Quote */}
          <div className="philosophy-hero">
            <div className="section-header">
              <div className="section-badge">
                <svg className="badge-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <span className="badge-text">Our Philosophy</span>
              </div>
              <h2 className="section-title">
                Building with <span className="title-accent">Purpose & Principles</span>
              </h2>
              <p className="section-description">
                Our philosophy guides every decision we make, every line of code we write, 
                and every solution we deliver.
              </p>
            </div>
            
            <div className="philosophy-quote-section">
              <div className="quote-container">
                <svg className="quote-mark" viewBox="0 0 120 120">
                  <path d="M21.4 58.9c0-7.7 2.5-13.8 7.4-18.3 4.9-4.5 11.6-6.8 20.2-6.8v-8.1c-8.5 0-15.8 1.8-22 5.4-6.2 3.6-11 8.7-14.6 15.1-3.5 6.5-5.3 13.8-5.3 22 0 8.9 2.4 16.4 7.1 22.5 4.7 6.1 11.4 9.1 20.1 9.1h8.6V58.9H21.4z"/>
                  <path d="M93.1 58.9c0-7.7 2.5-13.8 7.4-18.3 4.9-4.5 11.6-6.8 20.2-6.8v-8.1c-8.5 0-15.8 1.8-22 5.4-6.2 3.6-11 8.7-14.6 15.1-3.5 6.5-5.3 13.8-5.3 22 0 8.9 2.4 16.4 7.1 22.5 4.7 6.1 11.4 9.1 20.1 9.1h8.6V58.9H93.1z"/>
                </svg>
                <blockquote className="philosophy-quote">
                  Technology should reduce noise, not add to it.
                </blockquote>
                <cite className="quote-attribution">— Thought Paradigm Core Belief</cite>
              </div>
              
              <div className="philosophy-description">
                <p>
                  We build tools that <strong>simplify complexity</strong> — enabling organizations to focus on 
                  <em>creativity</em>, <em>collaboration</em>, and <em>clarity</em>. Every line of code serves 
                  a purpose: to make the complex simple and the impossible achievable.
                </p>
              </div>
            </div>
          </div>
          
          {/* Guiding Principles */}
          <div className="principles-section">
            <div className="section-header">
              <h3 className="principles-title">Guiding Principles</h3>
              <p className="principles-subtitle">
                Three fundamental beliefs that shape our approach to AI and enterprise technology.
              </p>
            </div>
            <div className="principles-grid">
              {principles.map((principle, index) => {
                const icons = [
                  <svg viewBox="0 0 20 20" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2v2h2V6H5zM3 13a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zm2 2v2h2v-2H5zM13 4a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V5a1 1 0 00-1-1h-4zm2 2h2v2h-2V6zM11 13a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4zm2 2h2v2h-2v-2z" clipRule="evenodd"/></svg>,
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                ];
                return (
                  <div key={index} className="principle-card">
                    <div className="principle-header">
                      <div className="principle-icon">{icons[index]}</div>
                      <div>
                        <h4 className="principle-title">{principle.title}</h4>
                        <p className="principle-description">{principle.description}</p>
                      </div>
                    </div>
                    <p className="principle-detail">{principle.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Core Values */}
          <div className="core-values-section">
            <div className="section-header">
              <h3 className="values-title">Core Values</h3>
              <p className="values-subtitle">
                The values that drive our daily decisions and long-term vision.
              </p>
            </div>
            <div className="values-grid">
              {coreValues.map((value, index) => (
                <div key={index} className="value-card">
                  <div className="value-header">
                    <h4 className="value-title">{value.title}</h4>
                    <p className="value-description">{value.description}</p>
                  </div>
                  <p className="value-detail">{value.detail}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Vision & Mission */}
          <div className="vision-mission">
            <div className="vision-card">
              <div className="card-icon">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="card-title">Our Vision</h3>
              <p className="card-content">
                To become a <strong>global leader</strong> in intelligent enterprise systems — 
                bridging human reasoning with machine understanding to create a future where 
                every organization can think, learn, and act intelligently.
              </p>
            </div>
            
            <div className="mission-card">
              <div className="card-icon">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="card-title">Our Mission</h3>
              <p className="card-content">
                To build <strong>trustworthy AI products</strong> that empower every organization 
                to unlock the full potential of their knowledge, transforming scattered information 
                into strategic advantage through intelligent, secure, and human-centered technology.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Philosophy;
